import React, { useState, useMemo } from 'react';
import {
  Stack,
  Typography,
  Alert,
  Chip,
  Tooltip,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { CloudDownload as ZohoIcon } from '@mui/icons-material';
import { EasyCheckDialog, EasyCheckButton } from '../../common/Controls';
import { zohoService } from '../../../services/zoho/zohoService';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';

// Import from Zoho (API Pull method): date range -> read-only preview -> import selected as Pending cheques.

const STATUS_META = {
  NEW: { label: 'New', color: 'success' },
  ALREADY_IMPORTED: { label: 'Already imported', color: 'default' },
  CHEQUE_NO_EXISTS: { label: 'Cheque no. exists', color: 'default' },
  BANK_NOT_MAPPED: { label: 'Bank not mapped', color: 'warning' },
  NO_CHEQUE_NUMBER: { label: 'No cheque no.', color: 'warning' },
  CURRENCY_MISMATCH: { label: 'Currency mismatch', color: 'warning' },
  INVALID: { label: 'Invalid', color: 'error' },
};

const RESULT_META = {
  IMPORTED: { label: 'Imported', color: 'success' },
  SKIPPED: { label: 'Skipped', color: 'default' },
  FAILED: { label: 'Failed', color: 'error' },
};

// Local yyyy-MM-dd (never toISOString — avoids the UTC+4 day shift)
const toIsoDate = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return { from: toIsoDate(from), to: toIsoDate(to) };
};

const ZohoImportDialog = ({ open, onClose, onImported }) => {
  const [stage, setStage] = useState('range'); // range | loading | review | importing | done
  const [range, setRange] = useState(defaultRange);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // { rows, totalFetched, nonChequeCount, truncated }
  const [selected, setSelected] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);

  const reset = () => {
    setStage('range');
    setError('');
    setPreview(null);
    setSelected(new Set());
    setShowAll(false);
    setProgress({ done: 0, total: 0 });
    setResult(null);
  };

  const handleClose = () => {
    if (stage === 'loading' || stage === 'importing') return;
    const imported = result?.importedCount > 0;
    reset();
    onClose();
    if (imported && onImported) onImported();
  };

  const handlePreview = async () => {
    setError('');
    if (!range.from || !range.to) {
      setError('Choose a From and To date.');
      return;
    }
    if (range.to < range.from) {
      setError('To date must be on or after From date.');
      return;
    }
    setStage('loading');
    try {
      const data = await zohoService.preview(range.from, range.to);
      setPreview(data);
      setSelected(new Set(data.rows.filter((r) => r.status === 'NEW').map((r) => r.zohoPaymentId)));
      setStage('review');
    } catch (e) {
      setError(e.message);
      setStage('range');
    }
  };

  const handleImport = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setError('');
    setStage('importing');
    setProgress({ done: 0, total: ids.length });
    try {
      const data = await zohoService.importPayments(ids, (done, total) => setProgress({ done, total }));
      setResult(data);
      setStage('done');
    } catch (e) {
      setError(e.message);
      setStage('review');
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const rows = useMemo(() => preview?.rows || [], [preview]);
  const newRows = useMemo(() => rows.filter((r) => r.status === 'NEW'), [rows]);
  const visibleRows = showAll ? rows : newRows;
  const allNewSelected = newRows.length > 0 && newRows.every((r) => selected.has(r.zohoPaymentId));

  const toggleAll = () => {
    setSelected(allNewSelected ? new Set() : new Set(newRows.map((r) => r.zohoPaymentId)));
  };

  const statusCounts = useMemo(() => {
    const counts = {};
    rows.forEach((r) => {
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  }, [rows]);

  const renderRangeStage = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Read cheque vendor payments from Zoho Books for a date range. Nothing is created until you review the list
        and click Import.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="From date"
          type="date"
          size="small"
          value={range.from}
          onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="To date"
          type="date"
          size="small"
          value={range.to}
          onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Up to 92 days per preview. Payments already imported are recognised and never imported twice.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );

  const renderLoading = (text) => (
    <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );

  const renderReviewStage = () => (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {preview.truncated && (
        <Alert severity="warning">
          This range has more payments than can be read at once. Only the first part is shown — choose a shorter range
          to see the rest.
        </Alert>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <Chip size="small" variant="outlined" label={`Payments in range: ${preview.totalFetched}`} />
        <Chip size="small" variant="outlined" label={`Not cheque: ${preview.nonChequeCount}`} />
        {Object.entries(statusCounts).map(([status, count]) => (
          <Chip
            key={status}
            size="small"
            variant="outlined"
            color={STATUS_META[status]?.color || 'default'}
            label={`${STATUS_META[status]?.label || status}: ${count}`}
          />
        ))}
        <FormControlLabel
          sx={{ ml: 'auto' }}
          control={<Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
          label={<Typography variant="body2">Show all cheque payments</Typography>}
        />
      </Stack>

      {visibleRows.length === 0 ? (
        <Alert severity="info">
          {rows.length === 0
            ? 'No cheque payments were found in Zoho Books for this date range.'
            : 'No new payments to import. Turn on "Show all cheque payments" to see why the others are excluded.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={allNewSelected} indeterminate={!allNewSelected && selected.size > 0} onChange={toggleAll} disabled={newRows.length === 0} />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cheque #</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bank</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Payment #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => {
                const importable = row.status === 'NEW';
                const meta = STATUS_META[row.status] || { label: row.status, color: 'default' };
                const currency = row.bankCurrency || row.currencyCode;
                return (
                  <TableRow key={row.zohoPaymentId} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={importable && selected.has(row.zohoPaymentId)}
                        disabled={!importable}
                        onChange={() => toggleRow(row.zohoPaymentId)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row.paymentDate}</TableCell>
                    <TableCell>{row.vendorName}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.chequeNumber || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {currency ? `${currency} ` : ''}
                      {formatCurrencyAmount(row.amount, currency, { withSymbol: false })}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{row.bankAccountName || row.zohoAccountName || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{row.paymentNumber}</TableCell>
                    <TableCell>
                      <Tooltip title={row.statusMessage || ''} arrow>
                        <Chip size="small" label={meta.label} color={meta.color} variant={importable ? 'filled' : 'outlined'} />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );

  const renderImportingStage = () => (
    <Stack spacing={2} sx={{ py: 4 }}>
      <Typography variant="body2" color="text.secondary">
        Importing {progress.done} of {progress.total} payment(s)…
      </Typography>
      <LinearProgress variant="determinate" value={progress.total ? (progress.done / progress.total) * 100 : 0} />
    </Stack>
  );

  const renderDoneStage = () => (
    <Stack spacing={2}>
      <Alert severity={result.failedCount > 0 ? 'warning' : 'success'}>
        Imported {result.importedCount} cheque(s) as Pending
        {result.skippedCount > 0 ? `, skipped ${result.skippedCount}` : ''}
        {result.failedCount > 0 ? `, ${result.failedCount} failed` : ''}. Imported cheques are now in the list, ready to print.
      </Alert>
      {result.results.some((r) => r.status !== 'IMPORTED') && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cheque #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.results
                .filter((r) => r.status !== 'IMPORTED')
                .map((r) => (
                  <TableRow key={r.zohoPaymentId}>
                    <TableCell>{r.vendorName || r.zohoPaymentId}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.chequeNumber || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={RESULT_META[r.status]?.label || r.status} color={RESULT_META[r.status]?.color || 'default'} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{r.message}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );

  let actions;
  if (stage === 'range') {
    actions = (
      <>
        <EasyCheckButton buttonType="form" variant="outlined" onClick={handleClose}>
          Cancel
        </EasyCheckButton>
        <EasyCheckButton buttonType="action" variant="contained" onClick={handlePreview} startIcon={<ZohoIcon />}>
          Preview Payments
        </EasyCheckButton>
      </>
    );
  } else if (stage === 'review') {
    actions = (
      <>
        <EasyCheckButton buttonType="form" variant="outlined" onClick={() => setStage('range')}>
          ← Change Dates
        </EasyCheckButton>
        <EasyCheckButton buttonType="action" variant="contained" color="success" onClick={handleImport} disabled={selected.size === 0}>
          Import Selected ({selected.size})
        </EasyCheckButton>
      </>
    );
  } else if (stage === 'done') {
    actions = (
      <EasyCheckButton buttonType="action" variant="contained" onClick={handleClose}>
        Done
      </EasyCheckButton>
    );
  }

  return (
    <EasyCheckDialog
      open={open}
      onClose={handleClose}
      title={stage === 'done' ? 'Zoho Import Complete' : 'Import from Zoho Books'}
      titleIcon={<ZohoIcon />}
      variant="large"
      maxWidth={stage === 'range' ? 'sm' : 'lg'}
      fullWidth
      actions={actions}
      disableBackdropClick
    >
      {stage === 'range' && renderRangeStage()}
      {stage === 'loading' && renderLoading('Reading payments from Zoho Books…')}
      {stage === 'review' && preview && renderReviewStage()}
      {stage === 'importing' && renderImportingStage()}
      {stage === 'done' && result && renderDoneStage()}
    </EasyCheckDialog>
  );
};

export default ZohoImportDialog;
